import { Link, useLocation } from "wouter";
import { useState, useEffect } from "react";
import { useAuth } from "./LoginGate";
import { ROLE_LABELS, canSeeFinance, canManageUsers, type UserRole } from "../lib/constants";
import logoUrl from "../assets/skr-logo.svg";
import monogramUrl from "../assets/skr-monogram.svg";

const navSections = [
  {
    title: "OVERVIEW",
    links: [
      { href: "/", label: "Dashboard", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg> },
    ],
  },
  {
    title: "MANAGEMENT",
    links: [
      { href: "/clients", label: "Clients", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
      { href: "/cases", label: "Cases", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg> },
      { href: "/client-tracker", label: "Client Tracker", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
      { href: "/current-situation", label: "Current Situation", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="M7 15l4-4 4 4 5-5"/></svg> },
      { href: "/payments", label: "Payments", financeOnly: true, icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
      { href: "/deadlines", label: "Deadlines", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
    ],
  },
  {
    title: "ANALYTICS",
    links: [
      { href: "/reports", label: "Reports", financeOnly: true, icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
      // Teams shows monthly targets + achieved revenue — a finance view.
      // Staff don't need it and the page used to white-screen for them
      // when it tried to render a stripped `monthly_target`.
      { href: "/teams", label: "Teams", financeOnly: true, icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg> },
    ],
  },
  {
    title: "SYSTEM",
    links: [
      { href: "/users", label: "Users", manageUsersOnly: true, icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
      { href: "/settings", label: "Settings", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> },
    ],
  },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const [pathname] = useLocation();
  const { logout, user } = useAuth();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('sidebar_collapsed', String(collapsed)); } catch {}
  }, [collapsed]);

  // Auto-close drawer when route changes on mobile
  useEffect(() => {
    if (mobileOpen && onMobileClose) onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <aside className={`sidebar${collapsed ? ' sidebar-collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="sidebar-logo">
        {!collapsed && (
          // Sizing lives in CSS (.sidebar-logo img) so mobile + desktop
          // overrides in one place can change it without touching JSX.
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <img src={logoUrl} alt="SKR Law" style={{ objectFit: 'contain' }} />
          </div>
        )}
        {collapsed && (
          // Collapsed rail is too narrow for the full "SKR LAW" wordmark,
          // so we swap to the square red "SKR" monogram.
          <img src={monogramUrl} alt="SKR" style={{ objectFit: 'contain' }} />
        )}
        <button
          className="sidebar-toggle"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          }
        </button>
      </div>

      <nav className="sidebar-nav">
        {navSections.map((section) => (
          <div key={section.title}>
            {!collapsed && <div className="nav-section">{section.title}</div>}
            {collapsed && <div style={{ height: 14 }} />}
            {section.links
              .filter(link => {
                if ((link as { financeOnly?: boolean }).financeOnly && !canSeeFinance(user?.role)) return false;
                if ((link as { manageUsersOnly?: boolean }).manageUsersOnly && !canManageUsers(user?.role)) return false;
                return true;
              })
              .map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`nav-link${pathname === link.href ? " active" : ""}${collapsed ? " nav-link-icon-only" : ""}`}
                  title={collapsed ? link.label : undefined}
                >
                  {link.icon}
                  {!collapsed && link.label}
                </Link>
              ))}
          </div>
        ))}
      </nav>

      <div style={{ padding: '10px 8px', borderTop: '1px solid var(--border)' }}>
        {!collapsed && (
          <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{user?.full_name || user?.username}</span>
            <span style={{ marginLeft: 6, fontSize: 10, background: user?.role === 'owner' ? 'var(--primary-light)' : '#F3F4F6', color: user?.role === 'owner' ? 'var(--primary)' : 'var(--text-muted)', padding: '1px 6px', borderRadius: 10, fontWeight: 600, textTransform: 'uppercase' }}>{ROLE_LABELS[(user?.role as UserRole)] ?? user?.role}</span>
          </div>
        )}
        <button
          className={`nav-link${collapsed ? " nav-link-icon-only" : ""}`}
          onClick={logout}
          style={{ color: 'var(--danger)', width: '100%' }}
          title={collapsed ? 'Sign Out' : undefined}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          {!collapsed && 'Sign Out'}
        </button>
      </div>
    </aside>
  );
}
